import React, { useState, useRef, useEffect } from 'react';
import { Box, Tooltip } from '@mui/material';
import { format, addDays, differenceInDays } from 'date-fns';
import { Workstream, Assignee } from './types';
import { useWorkstreamTypes } from './hooks/useWorkstreamTypes';
import { useWorkspaceContext } from './context/WorkspaceContext';
import { getWorkstreamPosition, TimelineConfig, isCriticalPath } from './utils/timelineUtils';

interface WorkstreamBarProps {
  workstream: Workstream;
  workstreams: Workstream[];
  timelineConfig: TimelineConfig;
  today: Date;
  assignee: Assignee | null;
  onDelete?: (id: string) => Promise<void>;
  onEdit?: (workstream: Workstream) => void;
  onUpdate?: (workstream: Workstream) => Promise<void>;
}

const parseDate = (date: string | Date): Date => {
  return date instanceof Date ? date : new Date(date);
};

export const WorkstreamBar: React.FC<WorkstreamBarProps> = ({
  workstream,
  workstreams,
  timelineConfig,
  today,
  onEdit,
  onUpdate,
}) => {
  const { workspaceId } = useWorkspaceContext();
  const { workstreamTypes } = useWorkstreamTypes(workspaceId);
  const [isDragging, setIsDragging] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [dragState, setDragState] = useState<{
    startX: number;
    originalDate: Date;
    currentStart: Date;
    currentEnd: Date;
  } | null>(null);
  
  const barRef = useRef<HTMLDivElement>(null);
  
  const position = getWorkstreamPosition(
    workstream.startDate,
    workstream.endDate,
    timelineConfig
  );

  const start = parseDate(workstream.startDate);
  const end = parseDate(workstream.endDate);
  const duration = differenceInDays(end, start);

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    e.preventDefault();
    
    const startDate = parseDate(workstream.startDate);
    setDragState({
      startX: e.clientX,
      originalDate: startDate,
      currentStart: startDate,
      currentEnd: parseDate(workstream.endDate)
    });
    setIsDragging(true);
    setShowTooltip(false);
  };

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      e.preventDefault();
      if (!dragState || !barRef.current) return;

      const dx = e.clientX - dragState.startX;
      const daysShift = Math.round(dx / (100 / 30));

      const newStartDate = addDays(dragState.originalDate, daysShift);
      const newEndDate = addDays(newStartDate, duration);

      const newPosition = getWorkstreamPosition(newStartDate, newEndDate, timelineConfig);
      barRef.current.style.left = `${newPosition.left}px`;

      setDragState(prev => prev ? {
        ...prev,
        currentStart: newStartDate,
        currentEnd: newEndDate
      } : null);
    };

    const handleMouseUp = async () => {
      if (dragState && onUpdate) {
        await onUpdate({
          ...workstream,
          startDate: dragState.currentStart.toISOString(),
          endDate: dragState.currentEnd.toISOString()
        });
      }
      setIsDragging(false);
      setDragState(null);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragState, duration, onUpdate, workstream, today, timelineConfig]);

  return (
    <>
      {isDragging && dragState && (
        <Box
          sx={{
            position: 'fixed',
            zIndex: 9999,
            padding: '6px 12px',
            backgroundColor: theme => theme.palette.mode === 'dark'
              ? 'rgba(0, 0, 0, 0.9)'
              : 'rgba(255, 255, 255, 0.9)',
            color: theme => theme.palette.mode === 'dark'
              ? 'rgba(255, 255, 255, 0.9)'
              : 'rgba(0, 0, 0, 0.9)',
            border: '1px solid',
            borderColor: theme => theme.palette.mode === 'dark'
              ? 'rgba(255, 255, 255, 0.1)'
              : 'rgba(0, 0, 0, 0.1)',
            borderRadius: 1,
            fontSize: '0.75rem',
            fontWeight: 500,
            pointerEvents: 'none',
            transform: 'translate(-50%, -100%) translateY(-8px)',
            boxShadow: theme => `0 4px 8px ${
              theme.palette.mode === 'dark'
                ? 'rgba(0, 0, 0, 0.5)'
                : 'rgba(0, 0, 0, 0.15)'
            }`,
          }}
          style={{
            left: barRef.current 
              ? barRef.current.getBoundingClientRect().left + (barRef.current.getBoundingClientRect().width / 2)
              : 0,
            top: barRef.current 
              ? barRef.current.getBoundingClientRect().top 
              : 0,
          }}
        >
          {format(dragState.currentStart, 'MMM d')} - {format(dragState.currentEnd, 'MMM d')}
        </Box>
      )}

      <Tooltip 
        title={`${format(start, 'MMM d')} - ${format(end, 'MMM d')}`}
        open={showTooltip && !isDragging}
        onClose={() => setShowTooltip(false)}
        onOpen={() => !isDragging && setShowTooltip(true)}
      >
        <Box
          ref={barRef}
          sx={{
            position: 'absolute',
            height: 24,
            top: '50%',
            transform: 'translateY(-50%)',
            borderRadius: 1,
            backgroundColor: (theme) => {
              const workstreamType = workstreamTypes.find(t => t.name === workstream.type);
              const baseColor = workstreamType?.color || theme.palette.grey[300];
              return isCriticalPath(workstream, workstreams) 
                ? theme.palette.error.main
                : baseColor;
            },
            left: `${position.left}px`,
            width: `${position.width}px`,
            cursor: isDragging ? 'grabbing' : 'grab',
            opacity: () => {
              const isOnCriticalPath = isCriticalPath(workstream, workstreams);
              return isOnCriticalPath ? 0.95 : 0.8;
            },
            transition: isDragging ? 'none' : 'all 0.2s ease',
            zIndex: isDragging ? 1000 : 1,
            '&:hover': {
              opacity: 1,
              transform: 'translateY(-50%) translateY(-1px)',
              boxShadow: theme => `0 2px 4px ${
                theme.palette.mode === 'dark' 
                  ? 'rgba(0, 0, 0, 0.3)'
                  : 'rgba(0, 0, 0, 0.1)'
              }`
            },
            '&::after': {
              content: '""',
              display: isCriticalPath(workstream, workstreams) ? 'block' : 'none',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              borderRadius: 'inherit',
              animation: 'pulse 2s infinite',
              border: '2px solid',
              borderColor: 'error.main',
              opacity: 0.5,
            },
            '@keyframes pulse': {
              '0%': {
                transform: 'scale(1)',
                opacity: 0.5,
              },
              '50%': {
                transform: 'scale(1.05)',
                opacity: 0.25,
              },
              '100%': {
                transform: 'scale(1)',
                opacity: 0.5,
              }
            }
          }}
          onMouseDown={handleMouseDown}
          onMouseEnter={() => !isDragging && setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
          onDoubleClick={() => onEdit && onEdit(workstream)}
        />
      </Tooltip>
    </>
  );
};